import { useRef, useState } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { useRouterState } from '@tanstack/react-router';
import { api } from '@/auth/api';
import { useActiveFleet } from '@/data/fleets';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * "Something not right?" (V2-U7) — the dispatcher twin of the driver
 * sheet. Deliberately low-threshold: one free-text field, optional
 * screenshot, no severity or category; context auto-attached.
 */
export function FeedbackDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { fleet } = useActiveFleet();
  const fileRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Unit D: re-entrant sends are dropped while one is in flight (replaces
  // the old busy state).
  const sendLock = useActionLock();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setError(null);
    try {
      let screenshotKey: string | null = null;
      if (file) {
        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const init = await api<{
          key: string;
          uploadUrl: string;
          uploadHeaders: Record<string, string>;
        }>('/v1/support-reports/screenshot-init', {
          method: 'POST',
          body: { mimeType, sizeBytes: file.size },
        });
        const put = await fetch(init.uploadUrl, {
          method: 'PUT',
          headers: init.uploadHeaders,
          body: file,
        });
        if (put.ok) screenshotKey = init.key;
      }
      await api('/v1/support-reports', {
        method: 'POST',
        body: {
          message: message.trim(),
          app: 'dispatcher',
          fleetId: fleet?.id ?? null,
          appVersion: __APP_VERSION__,
          build: __BUILD_TIME__,
          screen: pathname,
          deviceOs: navigator.userAgent.slice(0, 80),
          screenshotKey,
        },
      });
      setSent(true);
      setMessage('');
      setFile(null);
    } catch {
      setError("Couldn't send it. Try again.");
    }
  }

  function close() {
    setSent(false);
    setError(null);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Contact support"
      description={
        sent ? undefined : "Report a problem or ask a question — we reply by email."
      }
    >
      {sent ? (
        <div className="space-y-4">
          <p className="text-sm text-breaker-ink">
            Sent — Breaker support will reply by email.
          </p>
          <div className="flex justify-end">
            <Button size="sm" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            maxLength={4000}
            placeholder="Tell us what's happening — nothing's too small to mention."
            className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm text-breaker-ink placeholder:text-breaker-mute focus:outline-none focus:ring-2 focus:ring-breaker-line-2"
          />
          <div className="flex items-center gap-3 text-sm">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <>
                <span className="text-breaker-ink-2">{file.name}</span>
                <button
                  type="button"
                  className="text-breaker-mute underline underline-offset-2"
                  onClick={() => {
                    setFile(null);
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                >
                  Remove
                </button>
              </>
            ) : (
              <button
                type="button"
                className="text-breaker-ink-2 underline underline-offset-2"
                onClick={() => fileRef.current?.click()}
              >
                + Add a screenshot (optional)
              </button>
            )}
          </div>
          {error ? <p className="text-sm text-breaker-red">{error}</p> : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={close} disabled={sendLock.locked}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => sendLock.run(send)}
              loading={sendLock.locked}
              disabled={message.trim().length === 0}
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
